import { z } from 'zod';
import { Academy } from '../types';
import { MAX_GALLERY_PHOTOS } from '../constants/media';
import { isReservedAcademySlug } from '../constants/reservedAcademySlugs';
import { isCountryEligibleForCourtSync } from '../utils/managedPaymentsEligibility';
import { parseEmbedOriginsText, validateEmbedOrigins } from '../utils/embedOrigins';
import { optionalE164PhoneSchema } from './phone';

const academySlugField = z
  .string()
  .min(1, 'URL slug is required')
  .max(50, 'Slug too long')
  .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens')
  .refine((v) => !isReservedAcademySlug(v), 'This label is reserved and cannot be used');

const academySubdomainField = z
  .string()
  .min(1, 'Subdomain label is required')
  .max(63, 'Subdomain label too long')
  .regex(/^[a-z0-9-]+$/, 'Subdomain can only contain lowercase letters, numbers, and hyphens')
  .refine((v) => !isReservedAcademySlug(v), 'This subdomain is reserved and cannot be used');

/**
 * Academy form validation schema
 * Used for creating and updating academies via forms
 */
export const academyFormSchema = z.object({
  name: z.string().min(1, 'Academy name is required').max(100, 'Name too long'),
  slug: academySlugField,
  description: z.string().max(1000, 'Description too long').optional(),
  address: z.string().max(200, 'Address too long').optional(),
  phone: optionalE164PhoneSchema,
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  website: z.string().url('Invalid website URL').optional().or(z.literal('')),
  facebook: z.string().url('Invalid Facebook URL').optional().or(z.literal('')),
  instagram: z.string().url('Invalid Instagram URL').optional().or(z.literal('')),
  twitter: z.string().url('Invalid Twitter URL').optional().or(z.literal('')),
  linkedin: z.string().url('Invalid LinkedIn URL').optional().or(z.literal('')),
  primaryColor: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid color format').optional().or(z.literal('')),
  secondaryColor: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid color format').optional().or(z.literal('')),
  logoUrl: z.string().url('Invalid logo URL').optional().or(z.literal('')),
  bannerUrl: z.string().url('Invalid banner URL').optional().or(z.literal('')),
  galleryUrls: z
    .array(z.string().url('Invalid image URL'))
    .max(MAX_GALLERY_PHOTOS, `You can add up to ${MAX_GALLERY_PHOTOS} gallery photos`)
    .optional(),
  // Location (country + state/province; region derived from country for US/AU)
  country: z.string().length(2, 'Country must be a 2-letter code').optional().or(z.literal('')),
  region: z.enum(['US', 'AU', 'GB']).optional(),
  stateOrProvince: z.string().max(10, 'State/province code too long').optional().or(z.literal('')),
  currency: z.enum(['USD', 'AUD', 'GBP']).optional(),
  // Sibling discount (per academy)
  siblingDiscountEnabled: z.boolean().optional(),
  siblingDiscountPercent: z.number().min(0).max(100).optional(),
  siblingDiscountMinChildren: z.number().int().min(2).optional(),
  siblingDiscountScope: z.enum(['same_term', 'same_academy']).optional(),
  googleMapsAddress: z.string().optional(),
  // Lesson Management Policies
  makeupLessonEnabled: z.boolean().optional(),
  makeupNotificationDeadlineHours: z.number().int().min(1).optional(),
  makeupCreditExpiryDays: z.number().int().min(1).optional(),
  makeupCreditExpiryMode: z.enum(['days', 'end_of_term']).optional(),
  makeupCreditMaxPerPeriod: z.number().int().min(1).optional(),
  makeupCreditMaxPeriodMode: z.enum(['days', 'enrolled_term']).optional(),
  makeupCreditMaxPeriodDays: z.number().int().min(1).optional(),
  minimumStudentsForLesson: z.number().int().min(1).optional(),
  lowEnrollmentAlertsEnabled: z.boolean().optional(),
  // Waitlist (when lesson is full)
  waitlistEnabled: z.boolean().optional(),
  waitlistExpiryHours: z.number().int().min(1).optional(),
  // When true, students can book even when lesson is at capacity (no waitlist needed)
  allowEnrolmentWhenFull: z.boolean().optional(),
  /** Group term bookings: charge proportionally by remaining sessions after term start */
  proRataEnabled: z.boolean().optional(),
  // Processing fee pass-through: when true, Stripe processing fees are passed through to students
  surchargePassThroughEnabled: z.boolean().optional(),
  /** Required when enabling surchargePassThroughEnabled from false. */
  acknowledgeSurchargePassThrough: z.literal(true).optional(),
  /** Public academy page: show "Our Coaches" section */
  showCoachesOnPublicPage: z.boolean().optional(),
  /** When true, coaches can mark their own session attendance */
  allowCoachSelfAttendance: z.boolean().optional(),
  /** Student-initiated private lesson booking */
  privateBookingEnabled: z.boolean().optional(),
  privateBookingMinDurationMinutes: z.number().int().min(15).max(480).optional(),
  privateBookingMaxDurationMinutes: z.number().int().min(15).max(480).optional(),
  privateBookingDurationStepMinutes: z.number().int().min(5).max(120).optional(),
  privateBookingApprovalRequired: z.boolean().optional(),
  privateBookingMinLeadHours: z.number().int().min(0).max(720).optional(),
  /** Enable transactional SMS to opted-in guardians (Professional+) */
  smsEnabled: z.boolean().optional(),
  legalBusinessName: z.string().max(255).optional().nullable(),
  receiptNotes: z.string().max(2000).optional().nullable(),
  websiteEmbedEnabled: z.boolean().optional(),
  embedAllowedOriginsText: z.string().optional(),
}).superRefine((data, ctx) => {
  const minD = data.privateBookingMinDurationMinutes;
  const maxD = data.privateBookingMaxDurationMinutes;
  if (minD != null && maxD != null && maxD < minD) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Maximum duration must be greater than or equal to minimum duration.',
      path: ['privateBookingMaxDurationMinutes'],
    });
  }
  if (data.lowEnrollmentAlertsEnabled) {
    const m = data.minimumStudentsForLesson;
    if (m == null || m < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a minimum of at least 1 student when low enrolment alerts are enabled.',
        path: ['minimumStudentsForLesson'],
      });
    }
  }
  const cap = data.makeupCreditMaxPerPeriod;
  if (cap != null && cap >= 1) {
    if (!data.makeupCreditMaxPeriodMode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose how the make-up credit limit is measured (rolling days or per enrolled term).',
        path: ['makeupCreditMaxPeriodMode'],
      });
    }
    const days = data.makeupCreditMaxPeriodDays;
    if (days == null || days < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a rolling window of at least 1 day (used for rolling-days mode and when a booking has no term).',
        path: ['makeupCreditMaxPeriodDays'],
      });
    }
  }
  if (data.country?.trim() && !isCountryEligibleForCourtSync(data.country)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'This country is not supported for CourtSync subscriptions.',
      path: ['country'],
    });
  }
  if (data.websiteEmbedEnabled) {
    const validation = validateEmbedOrigins(parseEmbedOriginsText(data.embedAllowedOriginsText ?? ''));
    if (validation.errors.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: validation.errors[0] ?? 'Invalid embed origins',
        path: ['embedAllowedOriginsText'],
      });
    }
  }
});

/**
 * Type inferred from academy form schema
 * Use this type for form data in React Hook Form and other form libraries
 */
export type AcademyFormData = z.infer<typeof academyFormSchema>;

/**
 * Academy creation request type
 * Matches the expected type for createAcademy service method
 */
export type AcademyCreateRequest = Partial<Omit<Academy, 'id' | 'createdAt' | 'updatedAt' | 'ownerId'>> & Pick<Academy, 'name' | 'slug'>;

/**
 * Academy update request type
 * Matches the expected type for updateAcademy service method
 */
export type AcademyUpdateRequest = Partial<Omit<Academy, 'id' | 'createdAt' | 'updatedAt'>>;

/** Owner-only branded subdomain settings (Academy Settings). */
export const academySubdomainSettingsSchema = z.object({
  subdomain: academySubdomainField,
  subdomainEnabled: z.boolean(),
});

export type AcademySubdomainSettingsData = z.infer<typeof academySubdomainSettingsSchema>;

/** Check subdomain label availability (no reserved / format validation only). */
export const academySubdomainLabelSchema = academySubdomainField;

